import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import { Box, IconButton, Paper, Stack, Typography } from "@mui/material";
import { useTranslationHook } from "../../hooks";
import { FlowTestResponse } from "../../types/model/flowTest";
import { formatDate } from "../../utils";

export const FlowTestInfo = ({ flowTest }: { flowTest: FlowTestResponse }) => {
  const { t } = useTranslationHook();

  const hasInfo = flowTest.startDate || flowTest.aquiferName || flowTest.testTypeName;

  if (!hasInfo) {
    return <Typography variant="body2">#{flowTest.id}</Typography>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Typography variant="body2">
        <strong>{t("flow-tests.table.start-date")}:</strong>{" "}
        {flowTest.startDate ? formatDate(flowTest.startDate) : "-"}
      </Typography>

      <Typography variant="caption" color="text.secondary">
        <strong>{t("flow-tests.table.aquifer")}:</strong> {flowTest.aquiferName ?? "-"}
      </Typography>

      <Typography variant="caption" color="text.secondary">
        <strong>{t("flow-tests.table.test-type")}:</strong> {flowTest.testTypeName ?? "-"}
      </Typography>
    </Box>
  );
};

interface FlowTestCardProps {
  flowTest: FlowTestResponse;
  onDelete: () => void;
}

const FlowTestCard = ({ flowTest, onDelete }: FlowTestCardProps) => {
  const hasInfo = flowTest.startDate || flowTest.aquiferName || flowTest.testTypeName;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 3,
        display: "flex",
        justifyContent: "space-between",
        alignItems: hasInfo ? "flex-start" : "center",
      }}
    >
      {hasInfo ? (
        <Stack spacing={0.5}>
          <FlowTestInfoWithIcons flowTest={flowTest} />
        </Stack>
      ) : (
        <Typography>#{flowTest.id}</Typography>
      )}

      <IconButton size="small" onClick={onDelete}>
        <CloseRoundedIcon />
      </IconButton>
    </Paper>
  );
};

const FlowTestInfoWithIcons = ({ flowTest }: { flowTest: FlowTestResponse }) => {
  const { t } = useTranslationHook();

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <CalendarMonthRoundedIcon fontSize="small" color="action" />
        <Typography variant="body2">
          <strong>{t("flow-tests.table.start-date")}:</strong>{" "}
          {flowTest.startDate ? formatDate(flowTest.startDate) : "-"}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <WaterDropRoundedIcon fontSize="small" color="action" />
        <Typography variant="body2">
          <strong>{t("flow-tests.table.aquifer")}:</strong> {flowTest.aquiferName ?? "-"}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <ScienceRoundedIcon fontSize="small" color="action" />
        <Typography variant="body2">
          <strong>{t("flow-tests.table.test-type")}:</strong> {flowTest.testTypeName ?? "-"}
        </Typography>
      </Stack>
    </>
  );
};

export default FlowTestCard;